import React from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { PrimaryButton, Card, Icon } from '@hz-design-system/web-ui';

import BundleFeature from './BundleFeature';
import BundlePriceBlock from './BundlePriceBlock';
import DiscountedBundlePriceBlock from './DiscountedBundlePriceBlock';
import trackEvent from '../../utils/trackEvent';
import { BUNDLE_BUY_BUTTON_CLICK } from '../../constants/events';
import bundleIcons from '../../../../server/constants/bundleIcons';

import Classes from './BundleOption.module.scss';

type BundleOptionProps = {
  bundleType: string;
  isBundleRecommended: boolean;
  bundleLabel: string;
  marketFitText: string;
  isDisabled: boolean;
  effectivePrice: string;
  originalPrice: string;
  onSelect: (params: { bundleType: string }) => void;
  features: {
    text: string;
    isDisabled: boolean;
    isNew: boolean;
  }[];
  isPriceDiscounted: boolean;
  discountPercentage: number;
  isCurrentActiveBundle: boolean;
};

const BundlePurchaseAction = ({
  bundleLabel,
  isDisabled,
  onClick,
  isCurrentActiveBundle,
}: {
  bundleLabel: string;
  isDisabled: boolean;
  onClick: (...args: any[]) => void;
  isCurrentActiveBundle: boolean;
}) => {
  const label = isCurrentActiveBundle ? 'purchaseBundle.bundles.labelCurrent' : 'purchaseBundle.bundles.labelBuy';
  const i18n = useI18nContext();

  return (
    <div className={Classes.action}>
      <PrimaryButton
        type="button"
        disabled={isDisabled}
        onClick={onClick}
        className={isDisabled ? Classes.disabledButton : undefined}
      >
        {i18n.t(label, { bundleLabel })}
      </PrimaryButton>
      <div className={Classes['action-labelCancel']}>{i18n.t('purchaseBundle.bundles.labelCancelAnytime')}</div>
    </div>
  );
};

const BundleOption = (props: BundleOptionProps) => {
  const {
    bundleType,
    bundleLabel,
    marketFitText,
    onSelect,
    effectivePrice,
    originalPrice,
    isPriceDiscounted,
    discountPercentage,
    isDisabled,
    features,
    isBundleRecommended,
    isCurrentActiveBundle,
  } = props;

  const handleClick = () => {
    trackEvent({
      eventAction: BUNDLE_BUY_BUTTON_CLICK,
      eventLabel: bundleType,
    });
    onSelect({ bundleType });
  };

  const classNames = isBundleRecommended ? `${Classes.root} ${Classes['root--isRecommended']}` : Classes.root;
  const i18n = useI18nContext();

  return (
    <div className={classNames}>
      {isBundleRecommended && (
        <div className={Classes.recommendedLabel}>{i18n.t('purchaseBundle.bundles.labelRecommended')}</div>
      )}
      <div className={Classes.icon}>
        <Icon name={bundleIcons[bundleType]} />
      </div>
      <Card variant="filled" className={Classes.main}>
        <div className={Classes.headerWrap}>
          <h2>{bundleLabel}</h2>
          <div>
            <Trans i18nKey={marketFitText} />
          </div>
        </div>
        <div className={Classes.priceContainer}>
          {isPriceDiscounted ? (
            <DiscountedBundlePriceBlock
              originalPrice={originalPrice}
              effectivePrice={effectivePrice}
              discountPercentage={discountPercentage}
            />
          ) : (
            <BundlePriceBlock price={effectivePrice} />
          )}
        </div>
        <div className={Classes.actionContainer}>
          <BundlePurchaseAction
            isDisabled={isCurrentActiveBundle || isDisabled}
            onClick={handleClick}
            isCurrentActiveBundle={isCurrentActiveBundle}
            bundleLabel={bundleLabel}
          />
        </div>
        <div className={Classes.featuresWrap}>
          {features.map((feature, index) => (
            <BundleFeature
              key={`bundle-feature-${index}`}
              text={feature.text}
              isDisabled={feature.isDisabled}
              isNew={feature.isNew}
            />
          ))}
        </div>
      </Card>
    </div>
  );
};

export default BundleOption;
